import { hasRecordedVerdict } from '../../boards/review.js';
import { normalizedVerdictLine } from '../../review-verdict.js';
import { recordExplicitRuleOverrides } from '../../rules/event-rules.js';
import { isTerminalFactoryRuleStage } from '../../rules/types.js';
import type { FactoryGithubEventName, FactoryGithubRuleContext, FactoryRuleHandler } from '../../rules/types.js';
import { isFactoryApproveVerdict } from './stale-reviews.js';

export type GithubRuleOverrides = Partial<
  Record<FactoryGithubEventName, FactoryRuleHandler<FactoryGithubRuleContext> | null | undefined>
>;
export type GithubEventRules = Readonly<
  Record<FactoryGithubEventName, FactoryRuleHandler<FactoryGithubRuleContext> | null>
>;

function trustedGithubActor(context: Pick<FactoryGithubRuleContext, 'actor'>): boolean {
  return context.actor.type === 'github' && context.actor.trusted;
}

function githubActorLogin(context: Pick<FactoryGithubRuleContext, 'actor'>): string | undefined {
  return context.actor.type === 'github' ? context.actor.login : undefined;
}

function retriageGithubIssue(context: FactoryGithubRuleContext) {
  if (!context.item || context.item.source !== 'github-issue' || !context.item.url) return;
  if (context.actor.type === 'github' && context.actor.factoryAuthored) return;
  // The stage checks below use Work lane names; other boards opt in with their own rules.
  if (context.board !== 'work') return;
  // Re-triage would take over a card that is being built or reviewed.
  const stage = context.item.stages?.length === 1 ? context.item.stages[0] : undefined;
  if (stage === 'execute' || stage === 'review') return;
  // A finished card has nothing left to triage; talk on a closed issue must not park a run on it.
  if (isTerminalFactoryRuleStage(context.item.stages)) return;

  const reason =
    context.event === 'issueEdited'
      ? context.issueChange?.title && context.issueChange.body
        ? 'issue title and body edited'
        : context.issueChange?.title
          ? 'issue title edited'
          : 'issue body edited'
      : context.event === 'issueCommentDeleted'
        ? 'comment deleted'
        : context.event === 'issueCommentEdited'
          ? 'comment edited'
          : 'comment created';
  return {
    type: 'invokeSkill',
    idempotencyKey: `${context.ingress.id}:factory-triage`,
    role: 'triage',
    skillName: 'factory-triage',
    arguments: `Re-triage GitHub issue (${context.item.url}) after ${reason}.`,
  } as const;
}

function createdAfterFactory(createdAt: string | undefined, factoryCreatedAt: string): boolean {
  if (!createdAt) return false;
  const sourceCreatedAt = Date.parse(createdAt);
  const projectCreatedAt = Date.parse(factoryCreatedAt);
  return Number.isFinite(sourceCreatedAt) && Number.isFinite(projectCreatedAt) && sourceCreatedAt > projectCreatedAt;
}

/**
 * Issue and pull request numbers repeat across repositories, so new cards are
 * keyed by repository. A card this repository already owns keeps its key —
 * including canonical `github-issue:N` / `github-pr:N` keys from before
 * scoping — but only when it is the same kind of source: pull request intake
 * runs with the authoring issue as `context.item`.
 */
function githubSourceKey(
  context: FactoryGithubRuleContext,
  source: 'github-issue' | 'github-pr',
  itemNumber: number,
): string {
  if (context.item?.source === source && context.item.sourceKey) return context.item.sourceKey;
  return `github:${context.repository.id}:${source === 'github-issue' ? 'issue' : 'pull-request'}:${itemNumber}`;
}

function issueOpened(context: FactoryGithubRuleContext) {
  if (!context.issue) return;
  // Everything arrives on the routed board's initial phase (Work › Intake when
  // no label route selects another board). Trust/timing stamps remain available
  // to custom board rules.
  return {
    type: 'upsertLinkedWorkItem',
    idempotencyKey: `${context.ingress.id}:issue-intake`,
    board: context.intake?.board ?? 'work',
    source: 'github-issue',
    sourceKey: githubSourceKey(context, 'github-issue', context.issue.number),
    title: context.issue.title,
    url: context.issue.url,
    stage: context.intake?.initialPhase ?? 'intake',
    metadata: {
      githubRepositoryId: context.repository.id,
      githubIssueNumber: context.issue.number,
      ...(context.issue.createdAt ? { sourceCreatedAt: context.issue.createdAt } : {}),
      ...(githubActorLogin(context) ? { author: githubActorLogin(context) } : {}),
      authorTrusted: trustedGithubActor(context),
      autoStartCandidate:
        trustedGithubActor(context) && createdAfterFactory(context.issue.createdAt, context.factory.createdAt),
      assignees: context.issue.assignees ?? [],
      labels: context.issue.labels ?? [],
    },
  } as const;
}

function issueClosed(context: FactoryGithubRuleContext) {
  if (!context.item || context.item.source !== 'github-issue' || !context.issue) return;
  // The card's board decides where a closed source sends it; boards that declare no
  // `sourceClosed` mapping keep the card where it is, as do cards already finished.
  const closure = context.boardClosure;
  if (!context.board || !closure || closure.itemTerminal) return;
  // Issue closure is a repository fact, not third-party input — no actor trust
  // gate. `not_planned` (and `duplicate`) means abandoned, everything else is
  // completed work.
  const canceled = context.issue.stateReason === 'not_planned' || context.issue.stateReason === 'duplicate';
  const target = canceled ? closure.canceled : closure.completed;
  return {
    type: 'transition',
    idempotencyKey: `${context.ingress.id}:issue-closed`,
    board: context.board,
    stage: target.phase,
    message: {
      text:
        `GitHub issue #${context.issue.number} was closed` +
        `${context.issue.stateReason ? ` (${context.issue.stateReason})` : ''}; ` +
        `this ${closure.boardTitle} card was moved to ${target.title}.`,
    },
  } as const;
}

function materializePullRequestIntake(
  context: FactoryGithubRuleContext,
  {
    idempotencyKey,
    autoStartCandidate,
    stage = 'intake',
  }: { idempotencyKey: string; autoStartCandidate: boolean; stage?: 'intake' | 'review' },
) {
  if (!context.pullRequest) return;
  return {
    type: 'upsertLinkedWorkItem',
    idempotencyKey,
    board: 'review',
    source: 'github-pr',
    sourceKey: githubSourceKey(context, 'github-pr', context.pullRequest.number),
    title: context.pullRequest.title,
    url: context.pullRequest.url,
    stage,
    metadata: {
      githubRepositoryId: context.repository.id,
      githubPullRequestNumber: context.pullRequest.number,
      ...(context.pullRequest.createdAt ? { sourceCreatedAt: context.pullRequest.createdAt } : {}),
      factoryAuthored: context.pullRequest.factoryAuthored,
      authorTrusted: trustedGithubActor(context),
      autoStartCandidate,
      state: context.pullRequest.state,
      draft: context.pullRequest.draft,
      merged: context.pullRequest.merged,
      assignees: context.pullRequest.assignees ?? [],
      requestedReviewers: context.pullRequest.requestedReviewers ?? [],
      labels: context.pullRequest.labels ?? [],
      headBranch: context.pullRequest.headBranch,
      baseBranch: context.pullRequest.baseBranch,
      ...(context.pullRequest.author ? { author: context.pullRequest.author } : {}),
    },
  } as const;
}

function pullRequestOpened(context: FactoryGithubRuleContext) {
  if (!context.pullRequest) return;
  // Opening a pull request is evaluated once per card it concerns. This rule
  // files the pull request's own Review card, which is the arrival — the
  // evaluation carrying `pullRequestIntake` — so the authoring Work item's own
  // evaluation only moves it out for review: Building means "no pull request
  // yet", Review means one is open. The card rests there through every round of
  // feedback — the builder is woken in place — until the merge closes it.
  if (context.item && context.pullRequestIntake !== true) {
    if (context.board !== 'work' || context.pullRequest.state !== 'open') return;
    if (!context.item.stages.includes('execute')) return;
    // Resolution can match a Work item by branch alone, so a fork's pull
    // request must not be able to move it: same bar as auto-starting a review.
    if (!trustedGithubActor(context) && !context.pullRequest.factoryAuthored) return;
    return {
      type: 'transition',
      idempotencyKey: `${context.ingress.id}:out-for-review`,
      board: 'work',
      stage: 'review',
    } as const;
  }
  // A GitHub App bot is never a collaborator, so Factory's own PRs score
  // untrusted; their authorship is the trust signal.
  const autoStartCandidate =
    (trustedGithubActor(context) || context.pullRequest.factoryAuthored) &&
    createdAfterFactory(context.pullRequest.createdAt, context.factory.createdAt);
  return materializePullRequestIntake(context, {
    idempotencyKey: `${context.ingress.id}:pull-request-intake`,
    autoStartCandidate,
  });
}

function pullRequestMerged(context: FactoryGithubRuleContext) {
  if (!context.item || !context.pullRequest?.merged) return;
  if (context.board === 'review') {
    // The event is bound to the PR's own Review card: a merged PR is finished
    // review work, so always move the card to Done. The message only reaches
    // an active session (if any) — cards without one just move, instead of
    // failing retries against a binding that never existed.
    return {
      type: 'transition',
      idempotencyKey: `${context.ingress.id}:pull-request-merged`,
      board: 'review',
      stage: 'done',
      message: {
        text:
          `Pull request #${context.pullRequest.number} merged; this Review card was moved to Done. ` +
          'No further review is needed unless follow-up work was requested.',
      },
    } as const;
  }
  // Custom boards define their own lifecycle and cannot accept a transition
  // targeting the built-in Work board. Let their custom rules handle the merge.
  if (context.board !== 'work') return;
  // Provenance bound the event to the originating Work item instead: the merge
  // is what finishes the work, so it closes the Work card alongside its Review card —
  // unless the card has since opened another pull request, which is still out.
  const openPullRequestNumber = context.item.metadata?.openPullRequestNumber;
  if (typeof openPullRequestNumber === 'number' && openPullRequestNumber !== context.pullRequest.number) {
    return {
      type: 'sendMessage',
      idempotencyKey: `${context.ingress.id}:work-merged`,
      role: 'work',
      message:
        `Pull request #${context.pullRequest.number} merged. Pull request #${openPullRequestNumber} is still open, ` +
        'so this Work card stays in Review until it merges.',
    } as const;
  }
  return {
    type: 'transition',
    idempotencyKey: `${context.ingress.id}:work-merged`,
    board: 'work',
    stage: 'done',
    message: { text: `Pull request #${context.pullRequest.number} merged; this Work card was moved to Done.` },
  } as const;
}

function addressReviewFeedback(context: FactoryGithubRuleContext) {
  if (!context.item || !context.pullRequest || !context.review) return;
  // Only the Work item that authored the PR can act on the feedback. Provenance
  // binds the event there; a Review card seeing its own posted review must not
  // react to it (that would loop the reviewer against itself).
  if (context.board !== 'work') return;
  // A closed or merged pull request has no branch left to push fixes to.
  if (context.pullRequest.state !== 'open' || context.pullRequest.merged) return;
  // `approved` needs no work, and `commented` (a review body with no verdict)
  // is how a reviewer leaves notes without blocking — only a verdict that asks
  // for changes should pull the author back in.
  if (context.review.state.toLowerCase() !== 'changes_requested') return;
  // The authoring thread is already subscribed to this PR (`gh pr create`
  // subscribes automatically), so it can read the individual line comments
  // from its own notification inbox — the message only has to wake it and
  // point at the review.
  return {
    type: 'sendMessage',
    idempotencyKey: `${context.ingress.id}:address-review-feedback`,
    role: 'work',
    priority: 'high',
    message:
      `Changes were requested on pull request #${context.pullRequest.number} (${context.review.url}). ` +
      'Read the review comments on this PR, address the ones you agree with, and push the fixes to the PR branch. ' +
      'Reply on GitHub to anything you are deliberately not changing, explaining why.',
  } as const;
}

/**
 * A Factory approval does not clear a change request left by a different
 * Factory identity (an earlier reviewer token, say), so GitHub would keep the
 * pull request blocked. Ask the dispatcher to dismiss those superseded reviews.
 */
function dismissStaleFactoryReviews(context: FactoryGithubRuleContext) {
  const { pullRequest, review, repository } = context;
  if (!pullRequest || !review || !review.author || !repository.installationId) return;
  if (review.state.toLowerCase() !== 'approved' || !isFactoryApproveVerdict(review.body)) return;
  if (!pullRequest.factoryAuthored || pullRequest.state !== 'open' || pullRequest.merged) return;
  return {
    type: 'dismissStaleReviews',
    idempotencyKey: `${context.ingress.id}:dismiss-stale-reviews`,
    installationId: repository.installationId,
    repository: repository.fullName,
    pullRequestNumber: pullRequest.number,
    approvingReviewId: String(review.id),
    approvingAuthor: review.author,
  } as const;
}

function pullRequestReviewSubmitted(context: FactoryGithubRuleContext) {
  return addressReviewFeedback(context) ?? dismissStaleFactoryReviews(context);
}

/**
 * Detects the `factory-review` handoff verdict in a comment body.
 *
 * GitHub forbids an app from reviewing a pull request it authored, so on
 * Factory-authored PRs the review skill falls back to posting its verdict as a
 * plain comment. That comment is the only signal the authoring agent gets, so
 * it has to be readable back out. The skill's handoff contract puts the verdict
 * on the first line (`Verdict: request changes`), so only that line is
 * inspected — a verdict quoted later in the findings must not count.
 */
function requestsChangesVerdict(body: string | undefined): boolean {
  // Tolerate the markdown the skill wraps the line in (`**Verdict: ...**`).
  const normalized = normalizedVerdictLine(body);
  if (!normalized) return false;
  // Match the verdict exactly so negated phrasings ("Verdict: do not request
  // changes") cannot wake the author.
  return /^verdict: ?(request changes|changes requested)$/.test(normalized);
}

function addressPullRequestComment(context: FactoryGithubRuleContext) {
  // A validated Factory mention is a review-entry request, not feedback for the
  // authoring Work session. Invalid or unrecognized comments retain the normal
  // feedback route below.
  if (context.reviewCommand) return reReviewRequestedPullRequest(context);
  if (!context.item || !context.pullRequest || !context.issueComment) return;
  // Provenance binds the comment to the Work item that authored the PR — the
  // only session that can act on it. A Review card must not react to comments
  // on the PR it is reviewing.
  if (context.board !== 'work') return;
  if (context.pullRequest.state !== 'open' || context.pullRequest.merged) return;
  // `factoryAuthored` is one bit for the whole Factory, so Factory's own
  // comments are indistinguishable between roles — waking on all of them would
  // let the Work agent's own progress comments wake itself in a loop. The one
  // exception is the review verdict the Review run had to post as a comment
  // because GitHub refused a self-review: that is the handoff, and it only ever
  // asks for changes once per review, so it cannot sustain a loop.
  if (
    context.actor.type === 'github' &&
    context.actor.factoryAuthored &&
    !requestsChangesVerdict(context.issueComment.body)
  ) {
    return;
  }
  return {
    type: 'sendMessage',
    idempotencyKey: `${context.ingress.id}:address-pull-request-comment`,
    role: 'work',
    priority: 'high',
    message:
      `${context.issueComment.author ?? 'Someone'} commented on pull request #${context.pullRequest.number} ` +
      `(${context.issueComment.url ?? context.pullRequest.url}). Read the comment, address it if you agree, and push ` +
      'the fixes to the PR branch. Reply on GitHub to anything you are deliberately not changing, explaining why.',
  } as const;
}

function pullRequestClosed(context: FactoryGithubRuleContext) {
  if (!context.item || !context.pullRequest || context.pullRequest.merged) return;
  if (context.board !== 'review') return;
  // A PR closed without merging is abandoned review work: clear the card off
  // the board instead of leaving it in Reviewing forever.
  return {
    type: 'transition',
    idempotencyKey: `${context.ingress.id}:pull-request-closed`,
    board: 'review',
    stage: 'canceled',
    message: {
      text:
        `Pull request #${context.pullRequest.number} was closed without merging; ` +
        'this Review card was moved to Canceled.',
    },
  } as const;
}

function reReviewRequestedPullRequest(context: FactoryGithubRuleContext) {
  // GitHub reviewer requests and Factory's exact mention command are both
  // explicit requests to enter the same Review lifecycle.
  const factoryReviewEntry = context.reviewRequest?.factoryReviewer || context.reviewCommand !== undefined;
  if ((context.item && context.board !== 'review') || !factoryReviewEntry) return;
  if (!context.pullRequest || context.pullRequest.state !== 'open' || context.pullRequest.merged) return;
  // Trusted (write/admin) requesters only: creating or re-entering review checks
  // out and executes PR code, the same bar pullRequestOpened applies to auto-review.
  if (!trustedGithubActor(context)) return;
  if (context.actor.type === 'github' && context.actor.factoryAuthored) return;
  if (!context.item) {
    // On this path the actor is the *requester*, so the materialized
    // `authorTrusted` stamp records their trust (always true past the gate
    // above), not the PR author's: a trusted maintainer requesting a Factory
    // review vouches for the PR.
    return materializePullRequestIntake(context, {
      idempotencyKey: `${context.ingress.id}:pull-request-review-requested-intake`,
      autoStartCandidate: true,
      stage: 'review',
    });
  }
  // Already in Reviewing with no verdict yet: a pass is pending or running.
  // Once a verdict is recorded the card rests in Reviewing, so a fresh request
  // re-enters the stage to start another pass.
  const reviewing = context.item.stages.length === 1 && context.item.stages[0] === 'review';
  if (reviewing && !hasRecordedVerdict(context.item)) return;
  return {
    type: 'transition',
    idempotencyKey: `${context.ingress.id}:re-review-requested`,
    board: 'review',
    stage: 'review',
    ...(reviewing ? { reenter: true } : {}),
  } as const;
}

function reReviewUpdatedPullRequest(context: FactoryGithubRuleContext) {
  if (!context.item || context.board !== 'review') return;
  if (!context.pullRequest || context.pullRequest.state !== 'open' || context.pullRequest.merged) return;
  // Intake has not started a review pass yet, so a push there is just more of
  // the code the first pass will read. A push to a card sitting in Reviewing is
  // different: it invalidates whatever that pass is reading, so re-enter the
  // stage to supersede it. `reviewPullRequest` cancels the stale run and picks
  // the right skill for the entry it sees.
  if (context.item.stages.some(stage => stage === 'intake')) return;
  // A fork's author controls its head branch, so an untrusted push must not be
  // able to start review passes on demand; a maintainer can still request one.
  if (!trustedGithubActor(context) && !context.pullRequest.factoryAuthored) return;
  const alreadyReviewing = context.item.stages.some(stage => stage === 'review');
  return {
    type: 'transition',
    idempotencyKey: `${context.ingress.id}:re-review-updated`,
    board: 'review',
    stage: 'review',
    // Re-entry is the point when the card is already Reviewing: the stage's
    // entry rule is what cancels the superseded pass and starts one on the code
    // that just landed.
    ...(alreadyReviewing ? { reenter: true } : {}),
  } as const;
}

export const defaultGithubRules = Object.freeze({
  issueOpened: issueOpened,
  issueEdited: retriageGithubIssue,
  issueClosed: issueClosed,
  issueCommentCreated: retriageGithubIssue,
  issueCommentEdited: retriageGithubIssue,
  issueCommentDeleted: retriageGithubIssue,
  pullRequestOpened: pullRequestOpened,
  pullRequestUpdated: reReviewUpdatedPullRequest,
  pullRequestCommentCreated: addressPullRequestComment,
  pullRequestReviewRequested: reReviewRequestedPullRequest,
  pullRequestReviewSubmitted: pullRequestReviewSubmitted,
  pullRequestMerged: pullRequestMerged,
  pullRequestClosed: pullRequestClosed,
} satisfies GithubEventRules);

export function resolveGithubRules(overrides?: GithubRuleOverrides): GithubEventRules {
  if (overrides !== undefined && (overrides === null || typeof overrides !== 'object' || Array.isArray(overrides))) {
    throw new Error('GitHub rules must be an object.');
  }
  const rules: Record<string, FactoryRuleHandler<FactoryGithubRuleContext> | null> = { ...defaultGithubRules };
  for (const key of Reflect.ownKeys(overrides ?? {})) {
    if (typeof key !== 'string' || !Object.hasOwn(defaultGithubRules, key)) {
      throw new Error(`Unknown GitHub rule event: ${String(key)}.`);
    }
    const handler = overrides?.[key as FactoryGithubEventName];
    if (handler !== undefined && handler !== null && typeof handler !== 'function') {
      throw new Error(`GitHub rule ${key} must be a function, null, or undefined.`);
    }
    if (handler !== undefined) rules[key] = handler;
  }
  return recordExplicitRuleOverrides(Object.freeze(rules) as GithubEventRules, overrides);
}
